import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Wind,
  Square,
  Droplets,
  Waves,
  Hand,
  CalendarCheck,
  ShieldCheck,
  Wrench,
  BadgeCheck,
  CheckCircle2,
  ArrowRight,
  Phone,
  ChevronDown,
  MapPin,
  Building2,
} from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import SEO from "@/components/SEO";

const SITE_URL = "https://deepcleanrr.com";
const CANONICAL = `${SITE_URL}/services/carolina-beach`;

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_deep-hygiene/artifacts/rcjff4zo_Clearwater%20Bathroom.png";
const SANITIZE_IMG =
  "https://customer-assets.emergentagent.com/job_deep-hygiene/artifacts/dyf74fel_Clearwater%20restroom%20cleaning.png";
const FLOOR_IMG =
  "https://customer-assets.emergentagent.com/job_deep-hygiene/artifacts/v84o2ks0_Floor%20Clean%20Restroom.png";
const INDUSTRIES_IMG =
  "https://customer-assets.emergentagent.com/job_deep-hygiene/artifacts/oj5yjlm9_Janitor%20Cleaning%20Restroom.png";

const LAYERS = [
  {
    icon: Sparkles,
    n: "01",
    title: "Deep Restroom Sanitization",
    desc: "Hospital-grade disinfection of every fixture, surface, and corner. We target germs, bacteria, and hidden buildup to create a healthier, safer restroom environment for staff and visitors.",
  },
  {
    icon: Wind,
    n: "02",
    title: "Odor Elimination & Control",
    desc: "We eliminate odors at the source instead of simply masking them. Our advanced deodorizing treatments leave restrooms consistently fresh and clean long after service is complete.",
  },
  {
    icon: Square,
    n: "03",
    title: "Mirror & Glass Polishing",
    desc: "Enjoy streak-free, spot-free mirrors and glass partitions with every visit. We restore clarity and shine to all restroom glass surfaces for a polished, professional appearance.",
  },
  {
    icon: Droplets,
    n: "04",
    title: "Urinal & Toilet Descaling",
    desc: "Hard water stains, mineral deposits, and uric scale buildup are removed using professional-grade descaling methods that restore cleanliness and improve sanitation.",
  },
  {
    icon: Waves,
    n: "05",
    title: "Foam Cleaning & Floor Pressure Washing",
    desc: "Deep foam treatment and pressure washing break down embedded grime, bacteria, and buildup from restroom floors and hard-to-reach areas for a complete restoration clean.",
  },
  {
    icon: Hand,
    n: "06",
    title: "High-Touch Disinfection",
    desc: "Every high-contact surface is carefully disinfected, including door handles, faucets, soap dispensers, switches, partitions, and other frequently touched areas.",
  },
  {
    icon: CalendarCheck,
    n: "07",
    title: "Maintenance Programs",
    desc: "Choose from daily, weekly, or monthly restroom maintenance plans tailored to your facility's traffic, usage, and sanitation requirements.",
  },
];

const WHY = [
  { icon: ShieldCheck, title: "Hospital-Grade Disinfectants", desc: "Commercial-grade products and professional equipment built for real sanitation." },
  { icon: BadgeCheck, title: "Detail-Focused Technicians", desc: "Reliable, trained crews who notice the corners and edges others miss." },
  { icon: CalendarCheck, title: "Customized Maintenance", desc: "Schedules tailored to your facility's traffic, hours, and budget." },
  { icon: Sparkles, title: "Improved Appearance & Hygiene", desc: "Restrooms that look, smell, and feel genuinely clean — every visit." },
  { icon: Wrench, title: "Healthier Environments", desc: "Safer spaces for customers, employees, and visitors alike." },
  { icon: Building2, title: "Flexible Service Plans", desc: "Built for facilities of every size — from boutique offices to industrial properties." },
];

const INDUSTRIES = [
  "Office Buildings",
  "Restaurants & Cafés",
  "Retail Stores",
  "Schools & Universities",
  "Medical & Healthcare Facilities",
  "Gyms & Fitness Centers",
  "Warehouses & Industrial Facilities",
  "Apartment & Commercial Properties",
];

const SERVICE_AREAS = [
  "Carolina Beach",
  "Kure Beach",
  "Castle Hayne",
  "Wallace",
  "Rocky Point",
  "Porters Neck",
  "Belville",
  "Sneads Ferry",
];

const FAQS = [
  {
    q: "What does the seven-layer restroom sanitation include?",
    a: "It's our complete deep-clean program — sanitization, odor control, mirror/glass polishing, urinal & toilet descaling, foam cleaning with floor pressure washing, high-touch disinfection, and a recurring maintenance plan.",
  },
  {
    q: "How often should commercial restrooms be deep cleaned in Carolina Beach?",
    a: "Most Carolina Beach facilities benefit from daily janitorial maintenance with periodic deep sanitation — weekly or monthly — depending on traffic and industry.",
  },
  {
    q: "Do you eliminate odors permanently, or just mask them?",
    a: "We eliminate odors at the source. Foam cleaning breaks down urine salts, bacteria, and grime in grout and crevices so odors actually go away.",
  },
  {
    q: "Can you work after hours?",
    a: "Yes. We offer flexible scheduling including evenings, weekends, and overnight service so we don't interrupt your operations.",
  },
];

const Faq = ({ q, a, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div data-testid={`cw-faq-${idx}`} className="border-b border-slate-200 py-5">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="font-heading text-lg font-bold text-slate-900 md:text-xl">
          {q}
        </span>
        <ChevronDown
          size={20}
          strokeWidth={2}
          className={`shrink-0 text-blue-600 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <p className="mt-3 font-body text-base font-medium leading-relaxed text-slate-600">
          {a}
        </p>
      )}
    </div>
  );
};

const CarolinaBeachServicePage = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Commercial Restroom Sanitation",
    provider: {
      "@type": "LocalBusiness",
      name: "Deep Clean Restroom Services",
      telephone: "+19102369971",
      email: "info@deepcleanrr.com",
      url: SITE_URL,
    },
    areaServed: SERVICE_AREAS.map((a) => ({ "@type": "City", name: a })),
    url: CANONICAL,
    description:
      "Seven-layer restroom sanitation program for Carolina Beach, NC — deep disinfection, odor control, foam cleaning, descaling, high-touch disinfection, and tailored maintenance plans.",
  };

  return (
    <main data-testid="cw-service-page" className="bg-white">
      <SEO
        title="Restroom Sanitation Carolina Beach, NC | Seven-Layer Deep Clean"
        description="Deep Clean's seven-layer restroom sanitation program for Carolina Beach & New Hanover businesses — hospital-grade disinfection, foam cleaning, odor control, and maintenance plans."
        canonical={CANONICAL}
        image={HERO_IMG}
        type="website"
        jsonLd={jsonLd}
        keywords={[
          "restroom sanitation Carolina Beach",
          "commercial restroom cleaning Carolina Beach",
          "deep foam cleaning New Hanover",
          "Carolina Beach janitorial services",
          "restroom disinfection North Carolina",
        ]}
      />
      <Header />

      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-slate-950 pt-24">
        <div
          className="absolute inset-0 -z-10 bg-cover bg-center"
          aria-hidden="true"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div
          className="absolute inset-0 -z-10"
          aria-hidden="true"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,9,19,0.86) 0%, rgba(5,9,19,0.60) 30%, rgba(5,9,19,0.45) 100%), linear-gradient(90deg, rgba(5,9,19,0.80) 0%, rgba(5,9,19,0.40) 60%, rgba(5,9,19,0.10) 100%), radial-gradient(60% 50% at 85% 20%, rgba(6,182,212,0.22), transparent 70%)",
          }}
        />
        <div className="mx-auto max-w-7xl px-6 pb-24 pt-16 md:px-12 md:pb-32 md:pt-24">
          <p className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 font-body text-xs font-semibold uppercase tracking-[0.18em] text-white/90 backdrop-blur">
            <MapPin size={14} strokeWidth={2} className="text-cyan-300" />
            Carolina Beach, NC · New Hanover County
          </p>
          <h1
            data-testid="cw-hero-heading"
            className="max-w-4xl font-heading text-5xl font-black leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl xl:text-8xl"
          >
            Seven layers of
            <br />
            <span className="text-cyan-300">restroom sanitation.</span>
          </h1>
          <p className="mt-8 max-w-3xl font-body text-base font-medium leading-relaxed text-slate-200 md:text-lg">
            A clean restroom says everything about your business. Our complete
            seven-layer sanitation program eliminates bacteria, odors, buildup,
            and grime — leaving every surface spotless, sanitized, and
            guest-ready for Carolina Beach businesses.
          </p>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/#book"
              data-testid="cw-book-btn"
              className="group inline-flex h-14 items-center justify-center gap-3 rounded-md bg-blue-600 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-blue-700"
            >
              Request a Free Quote
              <ArrowRight size={18} strokeWidth={2.2} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="tel:9102369971"
              data-testid="cw-call-btn"
              className="inline-flex h-14 items-center justify-center gap-3 rounded-md border border-white/30 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-white/10"
            >
              <Phone size={18} strokeWidth={2} />
              Call 910-236-9971
            </a>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="border-b border-slate-200 bg-white py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:grid-cols-12 md:gap-16 md:px-12">
          <div className="md:col-span-5">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Built for every facility
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Cleaner restrooms.
              <br />
              <span className="text-slate-400">Better impressions.</span>
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
            <p>
              At Deep Clean, we deliver professional restroom sanitization
              services designed to eliminate bacteria, odors, buildup, and
              grime — leaving every surface spotless, sanitized, and
              guest-ready.
            </p>
            <p>
              Whether you manage an office, restaurant, retail store, school,
              gym, medical facility, or commercial property, our deep cleaning
              programs are built to maintain the highest standards of hygiene
              and presentation.
            </p>
          </div>
        </div>
      </section>

      {/* SANITIZATION FEATURE (with image) */}
      <section className="bg-slate-50 py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-6">
            <div className="overflow-hidden rounded-md border border-slate-200">
              <img
                src={SANITIZE_IMG}
                alt="Cleaning technician in yellow rubber gloves spraying disinfectant and scrubbing a commercial toilet bowl"
                data-testid="cw-sanitize-image"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-6">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Hospital-Grade Sanitization
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Disinfection that actually disinfects.
            </h2>
            <p className="mt-6 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              We target germs, bacteria, and hidden buildup with commercial
              disinfectants engineered for high-traffic restrooms. Every
              fixture, every surface, every corner — covered.
            </p>
            <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[
                "Toilets & urinals",
                "Sinks & faucets",
                "Walls & partitions",
                "Floors & grout",
                "Mirrors & glass",
                "Dispensers & switches",
              ].map((t, i) => (
                <div
                  key={t}
                  data-testid={`cw-coverage-${i}`}
                  className="flex items-start gap-3 rounded-md border border-slate-200 bg-white p-3"
                >
                  <CheckCircle2 size={18} strokeWidth={2} className="mt-0.5 shrink-0 text-blue-600" />
                  <span className="font-body text-sm font-semibold text-slate-800">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SEVEN LAYERS */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-12 grid grid-cols-1 items-end gap-8 md:grid-cols-12 md:gap-12">
            <div className="md:col-span-7">
              <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
                — What's Included
              </p>
              <h2
                data-testid="cw-layers-heading"
                className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl"
              >
                The seven layers.
                <br />
                <span className="text-blue-600">All in every visit.</span>
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
                One complete program. No piecemeal pricing. No upsells. Every
                Deep Clean visit covers all seven layers.
              </p>
            </div>
          </div>

          <ol
            data-testid="cw-layers-list"
            className="overflow-hidden rounded-md border border-slate-200 bg-white"
          >
            {LAYERS.map((s, i) => (
              <li
                key={s.title}
                data-testid={`cw-layer-${i}`}
                className="group grid grid-cols-[auto,auto,1fr,auto] items-start gap-5 border-b border-slate-200 p-6 transition-colors last:border-b-0 hover:bg-slate-50 md:p-8"
              >
                <span className="mt-1 font-heading text-sm font-bold tabular-nums text-slate-400">
                  {s.n}
                </span>
                <span className="grid h-11 w-11 place-items-center rounded-md border border-slate-200 bg-white text-blue-600 transition-colors group-hover:border-blue-600 group-hover:bg-blue-600 group-hover:text-white">
                  <s.icon size={20} strokeWidth={1.6} />
                </span>
                <div>
                  <h3 className="font-heading text-lg font-bold tracking-tight text-slate-900 md:text-xl">
                    {s.title}
                  </h3>
                  <p className="mt-1.5 font-body text-sm font-medium leading-relaxed text-slate-600">
                    {s.desc}
                  </p>
                </div>
                <span
                  className="mt-1 inline-flex h-6 items-center gap-1 rounded-full bg-blue-50 px-2.5 font-body text-[10px] font-bold uppercase tracking-wider text-blue-700"
                  data-testid={`cw-included-${i}`}
                >
                  <CheckCircle2 size={12} strokeWidth={3} />
                  Included
                </span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FOAM / FLOOR FEATURE */}
      <section className="border-y border-slate-200 bg-slate-50 py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Foam Cleaning & Pressure Washing
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Restoration-level cleaning.
            </h2>
            <p className="mt-6 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              Deep foam treatment combined with industrial pressure washing
              breaks down embedded grime, bacteria, and buildup on restroom
              floors and hard-to-reach areas — restoring tile, grout, and
              fixtures to a like-new condition.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Penetrates grout, crevices, and corners",
                "Removes urine salts and odor-causing bacteria",
                "Restores tile and floor appearance",
                "Reduces slip hazards from residue buildup",
              ].map((b, i) => (
                <li
                  key={b}
                  data-testid={`cw-foam-benefit-${i}`}
                  className="flex items-start gap-3 font-body text-base font-medium text-slate-700"
                >
                  <CheckCircle2 size={18} strokeWidth={2} className="mt-0.5 shrink-0 text-blue-600" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <figure className="overflow-hidden rounded-md border border-slate-200">
              <img
                src={FLOOR_IMG}
                alt="Cleaning technician using a powered floor scrubber on the tile floor of a commercial restroom"
                data-testid="cw-floor-image"
                className="h-full w-full object-cover"
              />
              <figcaption className="bg-slate-950 px-5 py-3 font-body text-xs font-bold uppercase tracking-[0.22em] text-cyan-300">
                Powered floor scrubbing · Industrial-grade
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* WHY DEEP CLEAN */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-12 grid grid-cols-1 items-end gap-8 md:grid-cols-12 md:gap-12">
            <div className="md:col-span-7">
              <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
                — Why Carolina Beach Businesses Choose Us
              </p>
              <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
                The standard for restroom hygiene.
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
                Equipment, products, and people you can count on — every visit.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-md bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
            {WHY.map((r, i) => (
              <div
                key={r.title}
                data-testid={`cw-why-${i}`}
                className="group bg-white p-8 transition-colors hover:bg-slate-950"
              >
                <span className="grid h-12 w-12 place-items-center rounded-md bg-blue-600/10 text-blue-600 transition-colors group-hover:bg-cyan-300/15 group-hover:text-cyan-300">
                  <r.icon size={22} strokeWidth={1.6} />
                </span>
                <h3 className="mt-6 font-heading text-lg font-bold tracking-tight text-slate-900 transition-colors group-hover:text-white lg:text-xl">
                  {r.title}
                </h3>
                <p className="mt-3 font-body text-sm font-medium leading-relaxed text-slate-600 transition-colors group-hover:text-white/80">
                  {r.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES (with image) */}
      <section className="border-y border-slate-200 bg-slate-50 py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-md border border-slate-200">
              <img
                src={INDUSTRIES_IMG}
                alt="Female janitor using a powered floor brush to clean a multi-stall commercial restroom"
                data-testid="cw-industries-image"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Industries We Serve
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Built for high-traffic facilities.
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {INDUSTRIES.map((ind, i) => (
                <div
                  key={ind}
                  data-testid={`cw-industry-${i}`}
                  className="group flex items-center gap-3 rounded-md border border-slate-200 bg-white p-3.5 transition-colors hover:border-blue-600"
                >
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-blue-600/10 text-blue-600 group-hover:bg-blue-600 group-hover:text-white">
                    <Building2 size={14} strokeWidth={2} />
                  </span>
                  <span className="font-body text-sm font-semibold text-slate-800">
                    {ind}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="bg-white py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-10 max-w-3xl">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Service Areas
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              Serving Carolina Beach &amp; the New Hanover coast.
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8">
            {SERVICE_AREAS.map((area, i) => (
              <div
                key={area}
                data-testid={`cw-area-${i}`}
                className="group flex items-center gap-2.5 rounded-md border border-slate-200 bg-white p-3 transition-colors hover:border-blue-600"
              >
                <MapPin size={14} strokeWidth={2} className="shrink-0 text-blue-600" />
                <span className="font-body text-[13px] font-semibold text-slate-800">
                  {area}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-slate-200 bg-slate-50 py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — FAQ
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Frequently Asked Questions.
            </h2>
            <p className="mt-6 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              Other questions? Call{" "}
              <a
                href="tel:9102369971"
                className="font-bold text-blue-600 hover:underline"
              >
                910-236-9971
              </a>
              .
            </p>
          </div>
          <div className="lg:col-span-8">
            {FAQS.map((f, i) => (
              <Faq key={f.q} q={f.q} a={f.a} idx={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-slate-950 py-20 text-white md:py-24">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(60% 50% at 80% 20%, rgba(6,182,212,0.30), transparent), radial-gradient(50% 40% at 0% 100%, rgba(0,85,255,0.40), transparent)",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 md:px-12 lg:flex-row lg:items-center">
          <div>
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300">
              — Carolina Beach, NC
            </p>
            <h2 className="mt-4 max-w-2xl font-heading text-3xl font-black tracking-tighter text-white sm:text-4xl lg:text-5xl">
              Ready to upgrade your restroom cleanliness standards?
            </h2>
            <p className="mt-4 max-w-xl font-body text-sm font-medium text-slate-300 md:text-base">
              Clean restrooms. Healthier spaces. Better impressions.
            </p>
          </div>
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
            <Link
              to="/#book"
              data-testid="cw-cta-book-btn"
              className="group inline-flex h-14 items-center justify-center gap-3 rounded-md bg-blue-600 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-blue-700"
            >
              Get a Free Quote
              <ArrowRight size={18} strokeWidth={2.2} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="tel:9102369971"
              data-testid="cw-cta-call-btn"
              className="inline-flex h-14 items-center justify-center gap-3 rounded-md border border-white/30 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-white/10"
            >
              <Phone size={18} strokeWidth={2} />
              910-236-9971
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default CarolinaBeachServicePage;
